import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, Image, X, Folder, Filter, FileText, Search, Upload } from 'lucide-react';
import InternalOnlyHint from '@/components/shared/InternalOnlyHint';
import { openPrivateFile } from '@/lib/privateFiles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CAMPAIGN_TYPES = ['Ticari Marka', 'Sivil Toplum', 'Sosyal Girişim'];
// Final reports live in the private "campaign-reports" bucket; the report row
// stores a "campaign-reports/<campaignId>/<file>" reference.
const REPORT_BUCKET = 'campaign-reports';
const REPORT_MAX_BYTES = 20 * 1024 * 1024;
const REPORT_TYPES = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

const reportStoragePath = (ref) =>
  ref && ref.startsWith(`${REPORT_BUCKET}/`) ? ref.slice(REPORT_BUCKET.length + 1) : null;

const reportFileName = (ref) => {
  const name = decodeURIComponent((ref || '').split('/').pop() || '');
  return reportStoragePath(ref) ? name.replace(/^\d+_/, '') : ref;
};

const PAYMENT_TYPES = ['Gelir Havuzu', 'Bütçe Havuzu', 'Sabit Ücret', 'Değişken Ücret', 'Gönüllü'];
const REDIRECT_TYPES = ['Web', 'App'];
const CATEGORIES = [
  'Moda', 'Güzellik', 'Sağlık', 'Teknoloji', 'Yemek', 'Seyahat',
  'Spor', 'Eğitim', 'Finans', 'Eğlence', 'Otomotiv', 'Ev & Yaşam',
  'Anne & Bebek', 'Evcil Hayvan', 'Sanat & Kültür', 'Diğer'
];

const emptyForm = {
  title: '',
  short_description: '',
  long_description: '',
  campaign_type: '',
  payment_type: '',
  category: '',
  start_date: '',
  end_date: '',
  total_budget: '',
  redirect_type: 'Web',
  redirect_url: '',
  param_mode: 'append_query',
  gallery_urls: [],
  participation_conditions: '',
};

const defaultPricingConfig = {
  campaign_params: { V0: 100, A: 100, B: 10, a: 0.5, b: 0.5 },
  scale_params: { enabled: true, S0: 10000, alpha: 0.5 },
  conversion_params: { enabled: true, C0: 100, beta: 0.5 },
  reach_quality_params: {
    enabled: true,
    lambda: 0.5,
    gamma: 1,
    L: 3,
    epsilon: 1e-9,
      stdThreshold: 1e-6,
      kpis: [
      { name: 'Takipçi Sayısı', source: 'followers', weight: 100 },
    ],
  },
  perf_quality_params: {
    enabled: true,
    lambda: 0.5,
    gamma: 1,
    L: 3,
    epsilon: 1e-9,
      stdThreshold: 1e-6,
      kpis: [
      { name: 'Tıklanma Sayısı', source: 'conversion', weight: 100 },
    ],
  },
};

const isPoolPayment = (paymentType) => ['Gelir Havuzu', 'Bütçe Havuzu', 'Gelir Payı', 'Bütçe Payı'].includes(paymentType);

const clonePricingConfig = (config = defaultPricingConfig) => JSON.parse(JSON.stringify(config));
const enforceAutoKpis = (config) => ({
  ...config,
  reach_quality_params: {
    ...config.reach_quality_params,
    enabled: true,
    kpis: [{ name: 'Takipçi Sayısı', source: 'followers', weight: 100 }],
  },
  perf_quality_params: {
    ...config.perf_quality_params,
    enabled: true,
    kpis: [{ name: 'Tıklanma Sayısı', source: 'conversion', weight: 100 }],
  },
});

const buildFormulaText = (config) => {
  const c = config.campaign_params || {};
  const s = config.scale_params || {};
  const p = config.conversion_params || {};
  return `Görev skoru = Baz TL × (${c.V0 ?? 0} + ${c.a ?? 0}×${c.A ?? 0}×(Takipçi/${s.S0 ?? 1})^${s.alpha ?? 0}×h_erişim + ${c.b ?? 0}×${c.B ?? 0}×(1+Tıklama/${p.C0 ?? 1})^${p.beta ?? 0}×h_performans). Hakediş = max(havuz payı, Baz TL).`;
};

export default function AdminCampaigns() {
  const { session } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [currentReport, setCurrentReport] = useState('');
  const [reportFile, setReportFile] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(clonePricingConfig());
  
  // Filters
  const [filterTitle, setFilterTitle] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPaymentType, setFilterPaymentType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRedirectType, setFilterRedirectType] = useState('all');

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*, campaign_gallery(image_url, display_order), campaign_reports(file_url)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Add hasReport flag based on campaign_reports
      const campaignsWithReportFlag = (data || []).map(c => ({
        ...c,
        hasReport: c.campaign_reports && c.campaign_reports.length > 0
      }));
      
      setCampaigns(campaignsWithReportFlag);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Kampanyalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Filter campaigns
  const filteredCampaigns = campaigns.filter(campaign => {
    if (filterTitle && !campaign.title?.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterType !== 'all' && campaign.campaign_type !== filterType) return false;
    if (filterPaymentType !== 'all' && campaign.payment_type !== filterPaymentType) return false;
    if (filterCategory !== 'all' && campaign.category !== filterCategory) return false;
    if (filterRedirectType !== 'all' && campaign.redirect_type !== filterRedirectType) return false;
    return true;
  });

  const openCreateDialog = () => {
    setSelectedCampaign(null);
    setForm(emptyForm);
    setPricingConfig(clonePricingConfig());
    setDialogOpen(true);
  };

  const openEditDialog = async (campaign) => {
    setSelectedCampaign(campaign);
    setPricingConfig(clonePricingConfig());
    const galleryUrls = campaign.campaign_gallery
      ?.sort((a, b) => a.display_order - b.display_order)
      .map(g => g.image_url) || [];
    setForm({
      title: campaign.title || '',
      short_description: campaign.short_description || '',
      long_description: campaign.long_description || '',
      campaign_type: campaign.campaign_type || '',
      payment_type: campaign.payment_type || '',
      category: campaign.category || '',
      start_date: campaign.start_date ? campaign.start_date.split('T')[0] : '',
      end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
      total_budget: campaign.total_budget || '',
      redirect_type: campaign.redirect_type || 'Web',
      redirect_url: campaign.redirect_url || '',
      param_mode: campaign.param_mode || 'append_query',
      gallery_urls: galleryUrls,
      participation_conditions: campaign.participation_conditions || '',
    });
    setDialogOpen(true);

    if (isPoolPayment(campaign.payment_type) && session?.access_token) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaign.id}/pricing-config`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        if (response.ok) {
          const config = await response.json();
          setPricingConfig(enforceAutoKpis({
            campaign_params: config.campaign_params || defaultPricingConfig.campaign_params,
            scale_params: config.scale_params || defaultPricingConfig.scale_params,
            conversion_params: config.conversion_params || defaultPricingConfig.conversion_params,
            reach_quality_params: config.reach_quality_params || defaultPricingConfig.reach_quality_params,
            perf_quality_params: config.perf_quality_params || defaultPricingConfig.perf_quality_params,
          }));
        }
      } catch (error) {
        console.error('Pricing config fetch error:', error);
      }
    }
  };

  const openDeleteDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setDeleteDialogOpen(true);
  };

  const openReportDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setCurrentReport(campaign.campaign_reports?.[0]?.file_url || '');
    setReportFile(null);
    setReportDialogOpen(true);
  };

  const handleReportFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (file && !REPORT_TYPES[file.type]) {
      toast.error('Sadece PDF, Excel, Word, PowerPoint, JPG veya PNG yükleyebilirsiniz');
      event.target.value = '';
      return;
    }
    if (file && file.size > REPORT_MAX_BYTES) {
      toast.error('Rapor dosyası 20 MB\'tan küçük olmalı');
      event.target.value = '';
      return;
    }
    setReportFile(file);
  };

  // Uploads the chosen file (or clears the report when removing), saves the
  // reference, then deletes the previously uploaded file.
  const handleSaveReport = async ({ remove = false } = {}) => {
    if (!selectedCampaign || (!remove && !reportFile)) return;

    setSaving(true);
    let uploadedPath = null;
    try {
      let reportUrl = null;
      if (!remove) {
        const safeName = reportFile.name.replace(/[^A-Za-z0-9._-]+/g, '-').slice(-80);
        uploadedPath = `${selectedCampaign.id}/${Date.now()}_${safeName}`;
        const { error } = await supabase.storage.from(REPORT_BUCKET).upload(uploadedPath, reportFile, {
          upsert: false,
          contentType: reportFile.type,
        });
        if (error) throw error;
        reportUrl = `${REPORT_BUCKET}/${uploadedPath}`;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/campaigns/${selectedCampaign.id}/final-report`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ report_url: reportUrl })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Report save error:', errorData);
        throw new Error(errorData.detail || 'Failed to save report');
      }

      const oldPath = reportStoragePath(currentReport);
      if (oldPath) await supabase.storage.from(REPORT_BUCKET).remove([oldPath]).catch(() => {});

      toast.success(reportUrl ? 'Final raporu yüklendi' : 'Final raporu kaldırıldı');
      setReportDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving report:', error);
      if (uploadedPath) await supabase.storage.from(REPORT_BUCKET).remove([uploadedPath]).catch(() => {});
      toast.error('Rapor kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updatePricingSection = (section, field, value) => {
    setPricingConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploadingGallery(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `campaigns/${selectedCampaign?.id || 'draft'}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from('campaign-gallery').upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'image/jpeg',
        });
        if (error) throw error;
        const { data } = supabase.storage.from('campaign-gallery').getPublicUrl(path);
        if (data?.publicUrl) uploadedUrls.push(data.publicUrl);
      }
      setForm(prev => ({ ...prev, gallery_urls: [...prev.gallery_urls, ...uploadedUrls] }));
      toast.success('Galeri görseli yüklendi');
    } catch (error) {
      console.error('Gallery upload error:', error);
      toast.error('Galeri görseli yüklenemedi');
    } finally {
      setUploadingGallery(false);
      event.target.value = '';
    }
  };

  const removeGalleryUrl = (index) => {
    setForm(prev => ({
      ...prev,
      gallery_urls: prev.gallery_urls.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!form.title || !form.campaign_type || !form.payment_type || !form.category) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    if (!form.redirect_url) {
      toast.error('Yönlendirme URL zorunludur');
      return;
    }

    if (form.payment_type === 'Bütçe Havuzu' && (!form.total_budget || parseFloat(form.total_budget) <= 0)) {
      toast.error('Bütçe Havuzu için toplam bütçe zorunludur');
      return;
    }

    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = session?.access_token;

      if (!token) {
        toast.error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
        setSaving(false);
        return;
      }

      const campaignData = {
        title: form.title,
        short_description: form.short_description || null,
        long_description: form.long_description || null,
        campaign_type: form.campaign_type,
        payment_type: form.payment_type,
        category: form.category,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : new Date().toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        total_budget: form.total_budget ? parseFloat(form.total_budget) : null,
        redirect_type: form.redirect_type,
        redirect_url: form.redirect_url,
        param_mode: form.param_mode,
        participation_conditions: form.participation_conditions || null,
      };

      let campaignId;

      if (selectedCampaign) {
        // Update existing campaign via backend API
        console.log('Updating campaign via backend API:', campaignData);
        const response = await fetch(`${backendUrl}/api/campaigns/${selectedCampaign.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(campaignData)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.detail || 'Kampanya güncellenemedi';
          toast.error(errorMessage, { duration: 6000 });
          setSaving(false);
          return;
        }

        const data = await response.json();
        console.log('Backend response:', data);

        campaignId = selectedCampaign.id;
        toast.success('Kampanya güncellendi');
      } else {
        // Create new campaign via backend API
        console.log('Creating campaign via backend API:', campaignData);
        const response = await fetch(`${backendUrl}/api/campaigns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(campaignData)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Kampanya oluşturulamadı');
        }

        const data = await response.json();
        console.log('Backend response:', data);

        campaignId = data.id;
        toast.success('Kampanya oluşturuldu');
      }

      // Handle gallery images via backend API
      if (form.gallery_urls.length > 0) {
        console.log('Updating gallery via backend API:', form.gallery_urls);
        const galleryResponse = await fetch(`${backendUrl}/api/campaigns/${campaignId}/gallery`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ image_urls: form.gallery_urls })
        });

        if (!galleryResponse.ok) {
          const errorData = await galleryResponse.json().catch(() => ({}));
          console.error('Gallery error:', errorData);
          toast.warning('Kampanya kaydedildi ama galeri güncellenemedi');
        } else {
          console.log('Gallery updated successfully');
        }
      }

      setDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.message || 'Kampanya kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCampaign) return;

    try {
      // Delete gallery first
      await supabase
        .from('campaign_gallery')
        .delete()
        .eq('campaign_id', selectedCampaign.id);

      // Delete campaign
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', selectedCampaign.id);

      if (error) throw error;

      toast.success('Kampanya silindi');
      setDeleteDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Kampanya silinirken hata oluştu');
    }
  };

  const toggleStatus = async (campaign) => {
    try {
      const now = Date.now();
      const endTime = campaign.end_date ? new Date(campaign.end_date).getTime() : null;
      const isExpired = endTime && endTime < now;
      const isClosing = campaign.status === 'open';
      const newStatus = isClosing || isExpired ? 'closed' : 'open';
      const updateData = {
        status: newStatus,
        manually_closed: isClosing,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id', campaign.id);

      if (error) throw error;

      if (!isClosing && isExpired) {
        toast.warning('Kampanya bitiş tarihi geçtiği için açılamaz');
      } else {
        toast.success(`Kampanya ${newStatus === 'open' ? 'açıldı' : 'kapatıldı'}`);
      }
      fetchCampaigns();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Durum değiştirilirken hata oluştu');
    }
  };

  const getStatusBadge = (status) => {
    return status === 'open' ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Açık</Badge>
    ) : (
      <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Kapalı</Badge>
    );
  };

  return (
    <div data-testid="admin-campaigns">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-brand text-2xl text-brand-ink">Kampanya Yönetimi</h2>
        <Button onClick={openCreateDialog} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kampanya
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-white rounded-lg border border-gray-200">
        <Filter className="w-4 h-4 text-gray-500" />
        
        {/* Title Search */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Başlık:</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Ara..."
              value={filterTitle}
              onChange={(e) => setFilterTitle(e.target.value)}
              className="w-32 pl-8"
              data-testid="filter-title-input"
            />
          </div>
        </div>
        
        {/* Type Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Tür:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36" data-testid="filter-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {CAMPAIGN_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Payment Type Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Ödeme:</Label>
          <Select value={filterPaymentType} onValueChange={setFilterPaymentType}>
            <SelectTrigger className="w-32" data-testid="filter-payment-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {PAYMENT_TYPES.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Kategori:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-36" data-testid="filter-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {CATEGORIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Redirect Type Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Yönlendirme:</Label>
          <Select value={filterRedirectType} onValueChange={setFilterRedirectType}>
            <SelectTrigger className="w-24" data-testid="filter-redirect-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {REDIRECT_TYPES.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{campaigns.length === 0 ? 'Henüz kampanya bulunmuyor' : 'Filtrelere uygun kampanya bulunamadı'}</p>
          {campaigns.length === 0 && (
            <Button onClick={openCreateDialog} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              İlk Kampanyayı Oluştur
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <Table data-testid="campaigns-table">
            <TableHeader>
              <TableRow>
                <TableHead>Başlık</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Ödeme</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Bitiş</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.map((campaign) => (
                <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                  <TableCell className="font-medium">{campaign.title}</TableCell>
                  <TableCell>{campaign.campaign_type}</TableCell>
                  <TableCell>{campaign.payment_type}</TableCell>
                  <TableCell>{campaign.category}</TableCell>
                  <TableCell>
                    <button onClick={() => toggleStatus(campaign)} className="cursor-pointer">
                      {getStatusBadge(campaign.status)}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openReportDialog(campaign)}
                        title={campaign.hasReport ? 'Final Raporu Düzenle' : 'Final Raporu Yükle'}
                        className={campaign.hasReport ? 'text-green-600' : ''}
                        data-testid={`upload-report-${campaign.id}`}
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/campaigns/${campaign.id}/report`)}
                        title="Katılımcı Raporu"
                        data-testid={`report-campaign-${campaign.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(campaign)}
                        data-testid={`edit-campaign-${campaign.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(campaign)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-campaign-${campaign.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCampaign ? 'Kampanyayı Düzenle' : 'Yeni Kampanya Oluştur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                maxLength={27}
                placeholder="Kampanya başlığı (max 27 karakter)"
                data-testid="campaign-title-input"
              />
            </div>

            {/* Short Description */}
            <div className="space-y-2">
              <Label htmlFor="short_description">Kısa Açıklama</Label>
              <Input
                id="short_description"
                value={form.short_description}
                onChange={(e) => handleInputChange('short_description', e.target.value)}
                maxLength={99}
                placeholder="Kısa açıklama (max 99 karakter)"
                data-testid="campaign-short-desc-input"
              />
            </div>

            {/* Long Description */}
            <div className="space-y-2">
              <Label htmlFor="long_description">Detaylı Açıklama</Label>
              <Textarea
                id="long_description"
                value={form.long_description}
                onChange={(e) => handleInputChange('long_description', e.target.value)}
                rows={4}
                placeholder="Detaylı açıklama"
                data-testid="campaign-long-desc-input"
              />
            </div>

            {/* Participation Conditions */}
            <div className="space-y-2">
              <Label htmlFor="participation_conditions">Katılım Koşulları</Label>
              <Textarea
                id="participation_conditions"
                value={form.participation_conditions}
                onChange={(e) => handleInputChange('participation_conditions', e.target.value)}
                rows={5}
                placeholder="Kullanıcının kampanyaya katılmadan önce kabul etmesi gereken koşulları yazın..."
                data-testid="campaign-conditions-input"
              />
              <p className="text-xs text-gray-500">
                Kullanıcı kampanyaya katılırken bu koşulları okuyup onaylaması gerekecek
              </p>
            </div>

            {/* Type, Payment, Category */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Kampanya Türü *</Label>
                <Select value={form.campaign_type} onValueChange={(v) => handleInputChange('campaign_type', v)}>
                  <SelectTrigger data-testid="campaign-type-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ödeme Türü *</Label>
                <Select value={form.payment_type} onValueChange={(v) => handleInputChange('payment_type', v)}>
                  <SelectTrigger data-testid="campaign-payment-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select value={form.category} onValueChange={(v) => handleInputChange('category', v)}>
                  <SelectTrigger data-testid="campaign-category-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  data-testid="campaign-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Bitiş Tarihi</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  data-testid="campaign-end-date-input"
                />
              </div>
            </div>

            {/* Budget */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_budget">Toplam Bütçe (TL)</Label>
                <Input
                  id="total_budget"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.total_budget}
                  onChange={(e) => handleInputChange('total_budget', e.target.value)}
                  placeholder="Opsiyonel"
                  data-testid="campaign-budget-input"
                />
              </div>
            </div>

            {/* Redirect */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Yönlendirme Türü
                  <InternalOnlyHint title="Uygulamada gösterilmez; kullanıcı her zaman Yönlendirme URL'sine gider. Yalnızca admin listesinde filtre için kullanılır." />
                </Label>
                <Select value={form.redirect_type} onValueChange={(v) => handleInputChange('redirect_type', v)}>
                  <SelectTrigger data-testid="campaign-redirect-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REDIRECT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redirect_url">Yönlendirme URL <span className="text-red-500">*</span></Label>
                <Input
                  id="redirect_url"
                  value={form.redirect_url}
                  onChange={(e) => handleInputChange('redirect_url', e.target.value)}
                  placeholder="https://..."
                  data-testid="campaign-redirect-url-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="param_mode">Parametre Modu</Label>
                <Select value={form.param_mode} onValueChange={(v) => handleInputChange('param_mode', v)}>
                  <SelectTrigger data-testid="campaign-param-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="append_query">URL'e Ekle (?token=...)</SelectItem>
                    <SelectItem value="macro_replace">Makro Değiştir ({'{TOKEN}'} yerine)</SelectItem>
                    <SelectItem value="no_param">Parametre Ekleme</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Tıklama takibi için token'ın URL'e nasıl ekleneceğini belirler
                </p>
              </div>
            </div>

            {/* Gallery Images */}
            <div className="space-y-2">
              <Label>Galeri Görselleri</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  disabled={uploadingGallery}
                  data-testid="gallery-file-input"
                />
                <Button type="button" variant="outline" disabled={uploadingGallery}>
                  <Image className="w-4 h-4 mr-2" />
                  {uploadingGallery ? 'Yükleniyor' : 'Yükle'}
                </Button>
              </div>
              {form.gallery_urls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.gallery_urls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Gallery ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/80?text=Error'}
                      />
                      <button
                        type="button"
                        onClick={() => removeGalleryUrl(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-gallery-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-campaign-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kampanyayı Sil</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            <strong>{selectedCampaign?.title}</strong> kampanyasını silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-campaign"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Final Raporu Yükle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              <strong>{selectedCampaign?.title}</strong> kampanyası için final raporu dosyasını yükleyin.
            </p>
            {currentReport && (
              <div className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm">
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-2 text-left text-blue-600 hover:underline"
                  onClick={() => (reportStoragePath(currentReport) ? openPrivateFile(currentReport) : window.open(currentReport, '_blank', 'noopener'))}
                  data-testid="current-report-link"
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="truncate">{reportFileName(currentReport)}</span>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => handleSaveReport({ remove: true })}
                  disabled={saving}
                  data-testid="remove-report-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Kaldır
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reportFile">{currentReport ? 'Yeni dosya ile değiştir' : 'Rapor dosyası'}</Label>
              <Input
                id="reportFile"
                type="file"
                accept={Object.keys(REPORT_TYPES).join(',')}
                onChange={handleReportFileChange}
                disabled={saving}
                data-testid="report-file-input"
              />
              <p className="text-xs text-gray-500">PDF, Excel, Word, PowerPoint, JPG veya PNG · en fazla 20 MB</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={() => handleSaveReport()} disabled={saving || !reportFile} data-testid="save-report-btn">
              {saving ? 'Yükleniyor...' : 'Yükle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
